import type { ItemDefinition, ItemId } from '../../../shared/game/supply-types.js';

/** What one occupied pack slot shows. A stack of three potions that share two-per-slot fills two slots: 2 and 1. */
export interface PackCell { itemId: ItemId; count: number }

/**
 * Lays a hero's carried counts out over their slots. The server only knows how much of each item is carried; where it sits is the
 * player's own arrangement, so this keeps whatever placements are still valid, drops the rest, and puts anything new in the first
 * free slot. Counts always fill earlier slots of an item first.
 */
export function arrangePack(layout: readonly (ItemId | null)[], items: Readonly<Record<ItemId, number>>, catalog: ReadonlyMap<ItemId, ItemDefinition>, capacity: number): { layout: (ItemId | null)[]; cells: (PackCell | null)[] } {
  const stack = (id: ItemId) => Math.max(1, catalog.get(id)?.stack ?? 1);
  const carried = [...catalog.keys()].filter(id => (items[id] ?? 0) > 0);
  const need = new Map(carried.map(id => [id, Math.ceil(items[id] / stack(id))]));
  const placed = new Map<ItemId, number>(), next: (ItemId | null)[] = Array.from({ length: capacity }, () => null);
  for (let slot = 0; slot < capacity; slot++) {
    const id = layout[slot];
    if (id && (placed.get(id) ?? 0) < (need.get(id) ?? 0)) { next[slot] = id; placed.set(id, (placed.get(id) ?? 0) + 1); }
  }
  for (const id of carried) while ((placed.get(id) ?? 0) < need.get(id)!) {
    const free = next.indexOf(null); if (free < 0) break;
    next[free] = id; placed.set(id, (placed.get(id) ?? 0) + 1);
  }
  const left = new Map(carried.map(id => [id, items[id]]));
  const cells = next.map(id => {
    if (!id) return null;
    const count = Math.min(stack(id), left.get(id)!); left.set(id, left.get(id)! - count);
    return { itemId: id, count };
  });
  return { layout: next, cells };
}

/** Swaps two slots, or does nothing for an out-of-range or identical pair. */
export function swapSlots(layout: readonly (ItemId | null)[], from: number, to: number): (ItemId | null)[] {
  const next = [...layout];
  if (from === to || from < 0 || to < 0 || from >= next.length || to >= next.length) return next;
  [next[from], next[to]] = [next[to], next[from]]; return next;
}
